import React, { useContext, useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import { APIServices } from "./../../helpers/APIServices";
import { UserContext } from "../../Layout";
import SimpleBackdrop from "../../Components/Backdrop";
import CustomDateHistoryAll from "./CustomDateHistoryAll";

export default function HistoryAll() {
  const [papers, setPapers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem("pending_searchTerm_1") || "");
  const [activeSearch, setActiveSearch] = useState(() => sessionStorage.getItem("pending_activeSearch_1") || "");
  
  // Pagination and Sorting State
  const [page, setPage] = useState(() => parseInt(sessionStorage.getItem("pending_page_1") || "0"));
  const [rowsPerPage, setRowsPerPage] = useState(() => parseInt(sessionStorage.getItem("pending_rowsPerPage_1") || "100"));
  const [order, setOrder] = useState(() => sessionStorage.getItem("pending_order_1") || "desc");
  const [orderBy, setOrderBy] = useState(() => sessionStorage.getItem("pending_orderBy_1") || "create_datetime");
  
  // Date Range State
  const [startDate, setStartDate] = useState(() => sessionStorage.getItem("pending_startDate_1") || "");
  const [endDate, setEndDate] = useState(() => sessionStorage.getItem("pending_endDate_1") || "");

  const user = useContext(UserContext);
  const role = user.qc_role;

  // Initialize status filters based on role or session
  const [statusFilters, setStatusFilters] = useState(() => {
    const saved = sessionStorage.getItem("pending_statusFilters_1");
    if (saved) return JSON.parse(saved);
    
    if (role === "checker") return ["1"];
    if (role === "approver") return ["2", "4"];
    if (role === "viewer") return ["1", "2", "3", "4", "5"];
    return ["1", "2", "4"]; // defaults for admin
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (statusFilters.length === 0) {
          setPapers([]);
          setTotal(0);
          setLoading(false);
          return;
      }

      // page + 1 because MUI uses 0-based index but Strapi/Custom API uses 1-based
      const response = await APIServices.getAllPaperCheckNull(
          1, 
          page + 1, 
          rowsPerPage, 
          activeSearch, 
          statusFilters,
          order,
          orderBy,
          role,
          startDate,
          endDate
      );

      setPapers(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, activeSearch, statusFilters, order, orderBy, startDate, endDate, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("pending_page_1", page.toString());
    sessionStorage.setItem("pending_rowsPerPage_1", rowsPerPage.toString());
    sessionStorage.setItem("pending_order_1", order);
    sessionStorage.setItem("pending_orderBy_1", orderBy);
    sessionStorage.setItem("pending_startDate_1", startDate);
    sessionStorage.setItem("pending_endDate_1", endDate);
    sessionStorage.setItem("pending_statusFilters_1", JSON.stringify(statusFilters));
    sessionStorage.setItem("pending_searchTerm_1", searchTerm);
    sessionStorage.setItem("pending_activeSearch_1", activeSearch);
  }, [page, rowsPerPage, order, orderBy, startDate, endDate, statusFilters, searchTerm, activeSearch]);

  // Submit search handler
  const handleSearch = () => {
    setPage(0); // Reset to first page on new search
    setActiveSearch(searchTerm);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setPage(0);
    setSearchTerm("");
    setActiveSearch("");
  };

  const partData = {
    data: papers,
    total: total,
  };

  return (
    <div>
      <SimpleBackdrop open={loading} setOpen={setLoading} />
      <Box sx={{ width: "100%" }}>
        <CustomDateHistoryAll
          partData={partData}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onSearch={handleSearch}
          onKeyDown={handleKeyDown}
          onClearSearch={handleClearSearch}
          activeSearch={activeSearch}
          statusFilters={statusFilters}
          setStatusFilters={(newFilters) => {
              setPage(0);
              setStatusFilters(newFilters);
          }}
          // Pass pagination/sorting props
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </Box>
    </div>
  );
}
